/**
 * Centralized formatting utilities for the application
 */

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  'K': '₩',  // Korean Won
  'U': '$',  // USD/USDT
  'B': '₿',  // Bitcoin
};

/**
 * Get currency symbol for a quote currency
 */
export function getCurrencySymbol(quote: string): string {
  return CURRENCY_SYMBOLS[quote] || '';
}

/**
 * Format price with appropriate currency symbol and decimals
 */
export function formatPrice(price: number, quote: string): string {
  const symbol = getCurrencySymbol(quote);
  
  if (quote === 'K') {
    // Korean Won - dynamic decimals based on price value
    let decimals: number;
    if (price < 0.0001) {
      decimals = 6;  // Very small values: 6 decimal points
    } else if (price < 1) {
      decimals = 4;  // Small values: 4 decimal points
    } else if (price < 10) {
      decimals = 3;  // Medium values: 2 decimal points
    } else if (price < 100) {
      decimals = 2;  // Large values: 1 decimal point
    } else {
      decimals = 0;  // Very large values: no decimal points
    }

    return `${symbol}${price.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  } else if (quote === 'B') {
    // Bitcoin - up to 8 decimals with compact notation for small values

    // For extremely small values, use compact notation: 0.0{n}xxx
    if (price < 0.0001 && price > 0) {
      const priceStr = price.toFixed(12).replace(/0+$/, ''); // Remove trailing zeros
      const match = priceStr.match(/^0\.(0+)(\d+)$/);

      if (match && match[1].length >= 4) {
        const zeroCount = match[1].length;
        const significantDigits = match[2].substring(0, 4); // Show 4 significant digits
        return `${symbol}0.0{${zeroCount}}${significantDigits}`;
      }
    }

    // Standard formatting - preserve up to 8 decimals, trim trailing zeros
    let formatted = price.toFixed(8);

    // Remove trailing zeros after decimal point
    formatted = formatted.replace(/(\.\d*?[1-9])0+$/, '$1'); // Remove trailing zeros after last non-zero digit
    formatted = formatted.replace(/\.0+$/, ''); // Remove decimal point if only zeros follow

    // Add thousand separators for the integer part
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${symbol}${parts.join('.')}`;
  } else {
    // USD/USDT - dynamic decimals based on price value (support up to 12 decimals)
    
    // For prices > 100, show 2 decimal points
    if (price > 100) {
      return `${symbol}${price.toLocaleString('en-US', { 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2 
      })}`;
    }
    
    // For extremely small values, use compact notation: 0.0{n}xxx
    if (price < 0.0001 && price > 0) {
      const priceStr = price.toFixed(12).replace(/0+$/, ''); // Remove trailing zeros
      const match = priceStr.match(/^0\.(0+)(\d+)$/);
      
      if (match && match[1].length >= 4) {
        const zeroCount = match[1].length;
        const significantDigits = match[2].substring(0, 4); // Show 4 significant digits
        return `${symbol}0.0{${zeroCount}}${significantDigits}`;
      }
    }
    
    // Standard formatting - preserve all precision, only trim trailing zeros
    // Use a high precision (12 decimals) to capture all significant digits
    let formatted = price.toFixed(12);
    
    // Remove trailing zeros after decimal point
    formatted = formatted.replace(/(\.\d*?[1-9])0+$/, '$1'); // Remove trailing zeros after last non-zero digit
    formatted = formatted.replace(/\.0+$/, ''); // Remove decimal point if only zeros follow
    
    // Add thousand separators for the integer part
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return `${symbol}${parts.join('.')}`;
  }
}

/**
 * Format volume - removes unnecessary trailing zeros and adds thousand separators
 * @param volume - The volume to format
 * @param maxDecimals - Maximum decimal places (default: 4)
 */
export function formatVolume(volume: number, maxDecimals: number = 4): string {
  // Format with thousand separators and remove unnecessary trailing zeros
  return volume.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals
  });
}

/**
 * Format total value with appropriate currency symbol and decimals
 * @param value - The total value to format
 * @param includeSymbol - Whether to include the currency symbol (default: true)
 * @param quote - Quote currency character ('K', 'U', 'B') - defaults to 'U' for USD/USDT
 */
export function formatTotalValue(value: number, includeSymbol: boolean = true, quote: string = 'U'): string {
  const symbol = getCurrencySymbol(quote);
  
  // Format based on currency type
  if (quote === 'K') {
    // Korean Won - no decimals for large values
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return includeSymbol ? `${symbol}${formatted}` : formatted;
  } else if (quote === 'B') {
    // Bitcoin - up to 8 decimals
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    });
    return includeSymbol ? `${symbol}${formatted}` : formatted;
  } else {
    // USD/USDT - always 2 decimal places
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return includeSymbol ? `${symbol}${formatted}` : formatted;
  }
}

/**
 * Format price for orderbook display (plain number, no symbol)
 */
export function formatOrderbookPrice(
  price: number,
  options: { minDecimals?: number; maxDecimals?: number } = {},
): string {
  const { minDecimals = 2, maxDecimals = 8 } = options;
  return price.toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format orderbook size using existing volume formatter
 */
export function formatOrderbookSize(volume: number): string {
  return formatVolume(volume, 6);
}

/**
 * Format orderbook total value with compact notation when appropriate
 */
export function formatOrderbookTotal(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000) {
    return formatCompactNumber(value);
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format percentage with specified decimals
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompactNumber(value: number): string {
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(2)}B`;
  } else if (value >= 1e6) {
    return `${(value / 1e6).toFixed(2)}M`;
  } else if (value >= 1e3) {
    return `${(value / 1e3).toFixed(2)}K`;
  }
  return value.toFixed(2);
}

/**
 * Format timestamp to relative time (e.g., "2m ago", "1h ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  } else if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)}h`;
  } else {
    return `${Math.floor(seconds / 86400)}d`;
  }
}

/**
 * Get profit color based on percentage - cyberpunk theme
 */
export function getProfitColor(percentage: number): string {
  if (percentage >= 10) return '#ff00ff';  // Pure magenta for >=10%
  if (percentage >= 5) return '#00ffff';   // Bright cyan for >=5%
  if (percentage >= 3) return '#00ff88';   // Neon green for >=3%
  return 'rgba(255, 255, 255, 0.9)';  // White with opacity for <3%
}
